/**
 * "Details": the process of a finished reply (reads, quiet writes, reasoning,
 * narration between calls) behind one quiet row, so the transcript leads with
 * the outcome and whatever needs a person. Closed by default; a reader who
 * opens it keeps it open.
 */
import { ChevronRight } from "lucide-react";
import { memo, useState, type FC, type ReactNode } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

type Props = {
  /** Tool calls inside, for the label ("6 steps"). */
  steps: number;
  /** Folded calls that changed something and can be undone inside. */
  changes?: number;
  children: ReactNode;
};

export const TurnDetails: FC<Props> = memo(
  ({ steps, changes = 0, children }) => {
    const [open, setOpen] = useState(false);
    return (
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="group/details min-w-0"
        data-testid="turn-details"
        data-steps={steps}
        data-changes={changes}
      >
        <CollapsibleTrigger
          className="-ml-1 flex h-(--row-h) w-[calc(100%+0.25rem)] min-w-0 items-center gap-1.5 rounded-sm px-1 text-left text-base text-fg-3 hover:bg-fill-1 hover:text-fg"
          data-testid="turn-details-trigger"
        >
          <ChevronRight
            className={cn(
              "size-3.5 shrink-0 text-fg-4 transition-transform",
              open && "rotate-90"
            )}
          />
          <span>Details</span>
          <span className="text-fg-4">
            {steps} {steps === 1 ? "step" : "steps"}
            {changes > 0
              ? ` · ${changes} ${
                  changes === 1 ? "change" : "changes"
                } to review`
              : ""}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-0.5">
          <div className="ml-1.5 flex flex-col gap-1.5 border-l border-line-2 pl-2">
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);
TurnDetails.displayName = "TurnDetails";
